import React, { ReactNode } from 'react';

import {
    TextField,
    ChipField,
    EditButton,
    ShowButton,
    Filter,
    TextInput,
    SelectInput,
    ReferenceInput,
    BooleanField,
    FunctionField,
    Link,
    BooleanInput,
} from 'react-admin';
import CustomizableGrid from '../components/CustomizableGrid';
import { ColumnConfig } from '../hooks/useColumnReorder';

import CityField from '../components/CityField';
import WarehousePagination from './WarehousePagination';
import CustomSearchInput from '../components/CustomSearchInput';
const WarehouseFilter = props => (
    <Filter variant="outlined" {...props}>
        <CustomSearchInput alwaysOn />
        <ReferenceInput
            source="cityId"
            reference="city"
            resource="city"
            label="City"
        >
            <SelectInput optionText={choice => `${choice.name}`} />
        </ReferenceInput>
        <ReferenceInput
            source="countryId"
            reference="country"
            resource="country"
            label="Country"
        >
            <SelectInput optionText={choice => `${choice.name}`} />
        </ReferenceInput>
        <BooleanInput source="isActive" label="Active" />
    </Filter>
);

// Custom field components
const CityNameField = ({ record }) => {
    if (!record || !record.city?.name) return null;
    return <span>{record.city.name}</span>;
};

const WarehouseList = ({ permissions = [], ...props }) => {
    // Define default columns configuration
    const defaultColumns: ColumnConfig[] = [
        { id: 'serial', label: 'Serial', visible: true, order: 0 },
        { id: 'id', label: 'ID', visible: true, order: 1 },
        { id: 'name', label: 'Name', visible: true, order: 2 },
        { id: 'city', label: 'City', visible: true, order: 3 },
        { id: 'active', label: 'Active', visible: true, order: 4 },
        { id: 'edit', label: 'Edit', visible: true, order: 5 },
        { id: 'show', label: 'Show', visible: true, order: 6 },
    ];

    // Function to render column based on column ID
    const renderColumn = (columnId: string): ReactNode => {
        switch (columnId) {
            case 'serial':
                return (
                    <TextField
                        key={columnId}
                        source="serial"
                        sortable={false}
                    />
                );
            case 'id':
                return <TextField key={columnId} source="id" />;
            case 'name':
                return <TextField key={columnId} source="name" />;
            case 'city':
                return (
                    <FunctionField
                        key={columnId}
                        label="City"
                        render={record => <CityNameField record={record} />}
                    />
                );
            case 'active':
                return <BooleanField key={columnId} source="active" />;
            case 'edit':
                return (
                    <EditButton
                        key={columnId}
                        color="secondary"
                        style={{ padding: 0 }}
                    />
                );
            case 'show':
                return <ShowButton key={columnId} style={{ padding: 0 }} />;
            default:
                return null;
        }
    };

    // Ensure we have all required props, but avoid duplicates with what might be in props
    const combinedProps = {
        title: 'Warehouses',
        permissions,
        defaultColumns,
        storageKey: 'warehouseListColumns',
        renderColumn,
        bulkActionButtons: false,
        filtersComponent: <WarehouseFilter />,
        sort: { field: 'id', order: 'ASC' },
        paginationComponent: <WarehousePagination />,
        perPage: 10,
        ...props,
        // Ensure these are set if not in props
        resource: props.resource || 'warehouses',
        location: props.location || { search: '' },
        hasShow: props.hasShow !== undefined ? props.hasShow : true,
    };

    return <CustomizableGrid {...combinedProps} />;
};

WarehouseList.defaultProps = {
    resource: 'warehouses',
    location: { search: '' },
};

export default WarehouseList;
